"use client";

import React, { ReactNode } from 'react';
import { Eye, EyeOff, GripVertical, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface DropdownMenuDraggableItemProps {
  id: string;
  label: string;
  icon?: ReactNode;
  // Right element type: 'eye' for visibility toggle, 'chevron' for chevron right, or custom ReactNode
  rightElement?: 'eye' | 'chevron' | ReactNode;
  isVisible?: boolean; // Only used when rightElement is 'eye'
  onToggleVisibility?: () => void; // Only used when rightElement is 'eye'
  onClick?: () => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  // Drag and drop handlers
  onDragStart?: (e: React.DragEvent) => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDragLeave?: (e: React.DragEvent) => void;
  onDragEnd?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent) => void;
  /** Pointer-drag from the grip (useSlidingReorder); disables the HTML5 drag. */
  onGripPointerDown?: (e: React.PointerEvent) => void;
  isDragOver?: boolean;
  isDragging?: boolean;
  isSelected?: boolean;
  className?: string;
  'aria-label'?: string;
  'aria-selected'?: boolean;
  tabIndex?: number;
}

export function DropdownMenuDraggableItem({
  id,
  label,
  icon,
  rightElement = 'eye',
  isVisible = false,
  onToggleVisibility,
  onClick,
  onKeyDown,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDragEnd,
  onDrop,
  onGripPointerDown,
  isDragOver = false,
  isDragging = false,
  isSelected = false,
  className,
  'aria-label': ariaLabel,
  'aria-selected': ariaSelected,
  tabIndex = 0,
}: DropdownMenuDraggableItemProps) {
  // Render right element based on type
  const renderRightElement = () => {
    if (rightElement === 'eye') {
      return (
        <div className="ml-auto min-w-0 flex-shrink-0 text-faint">
          <div className="flex items-center">
            <button
              type="button"
              aria-label={isVisible ? "Hide property" : "Show property"}
              title={isVisible ? "Hide property" : "Show property"}
              className="inline-flex items-center justify-center h-6 w-6 rounded-md transition-colors hover-component  cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                onToggleVisibility?.();
              }}
            >
              {isVisible ? (
                <Eye className="w-4 h-4 text-faint" />
              ) : (
                <EyeOff className="w-4 h-4 text-faint" />
              )}
            </button>
          </div>
        </div>
      );
    } else if (rightElement === 'chevron') {
      return (
        <div className="flex items-center gap-1 flex-shrink-0">
          <ChevronRight className="h-[15px] w-[15px] text-faint" aria-hidden="true" />
        </div>
      );
    } else if (rightElement) {
      // Custom ReactNode
      return (
        <div className="ml-auto min-w-0 flex-shrink-0 text-faint">
          {rightElement}
        </div>
      );
    }
    return null;
  };

  return (
    <div
      key={id}
      role="menuitem"
      tabIndex={tabIndex}
      className={cn(
        "flex items-center justify-between min-h-[38px] rounded-[7px] px-2.5 py-[7px] transition cursor-default",
        isDragOver ? "bg-gray-100 dark:bg-[rgb(42,42,42)]" : "hover-component ",
        isDragging && "opacity-50",
        isSelected && "bg-selected ",
        className
      )}
      draggable={!onGripPointerDown}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDragEnd={onDragEnd}
      onDrop={onDrop}
      onClick={onClick}
      onKeyDown={onKeyDown}
      aria-label={ariaLabel || label}
      title={ariaLabel || label}
      aria-selected={ariaSelected}
    >
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <span
          className="cursor-grab text-faint touch-none"
          aria-hidden="true"
          onPointerDown={onGripPointerDown}
          onClick={onGripPointerDown ? (e) => e.stopPropagation() : undefined}
        >
          <GripVertical className="w-4 h-4 text-faint" />
        </span>
        {icon && (
          <span aria-hidden="true" className="flex-shrink-0 flex items-center text-sec">
            {icon}
          </span>
        )}
        <span className="truncate text-sm text-gray-900 dark:text-gray-100">
          {label}
        </span>
      </div>
      {renderRightElement()}
    </div>
  );
}

